type TimerHandle = ReturnType<typeof setTimeout>;

type JobSaveState<T> = {
  revision: number;
  timer: TimerHandle | null;
  tail: Promise<void>;
  active: boolean;
  /**
   * The job as the engine last stored it: every successful save, else the
   * published job before the first commit was admitted. A failed save whose
   * rollback reload also fails falls back to it (chunk 28, R5-D1).
   */
  lastSaved: T | null;
};

export type RevisionedJobSaveOptions<T> = {
  idOf: (job: T) => string;
  save: (job: T) => Promise<T>;
  reload: (jobId: string) => Promise<T | null>;
  onSaved: (job: T) => void;
  /**
   * An explicit commit was admitted: publish it as the current job now. A save
   * superseded by a later one never reaches `onSaved`, so a later change built
   * from the published jobs would otherwise start from the older snapshot and the
   * engine, which stores whole jobs, would keep only the later change. A failed
   * save is put back by `onRollback`.
   */
  onCommitAdmitted?: (job: T) => void;
  /**
   * The published job before a commit is admitted. It seeds the rollback target
   * for a job not yet saved through this coordinator, so a commit published at
   * admission is still put back when its save fails and the reload throws.
   */
  currentOf?: (jobId: string) => T | null;
  onRollback: (jobId: string, job: T | null) => void;
  onAutosaveError: (reason: unknown) => void;
  /** Runs after save and any rollback reload have both settled. */
  onSettled?: (job: T) => void;
  /**
   * True from admission in the UI turn through the latest save and rollback.
   * A consumer can use this as a per-job write barrier without reconstructing
   * coordinator revisions from object identity.
   */
  onActivityChange?: (jobId: string, active: boolean) => void;
  debounceMs?: number;
  schedule?: (action: () => void, delayMs: number) => TimerHandle;
  cancel?: (timer: TimerHandle) => void;
};

/**
 * Orders saves independently for every job and applies a response only while it
 * is still the newest requested revision for that job.
 *
 * The engine stores whole job snapshots. Without this coordinator, a slower
 * response for an older snapshot can replace a newer draft in the renderer, and
 * a single global debounce timer lets editing one job cancel another job's save.
 */
export class RevisionedJobSaveCoordinator<T> {
  private readonly options: RevisionedJobSaveOptions<T>;
  private readonly states = new Map<string, JobSaveState<T>>();
  private readonly retired = new Set<string>();
  private readonly debounceMs: number;
  private readonly schedule: (action: () => void, delayMs: number) => TimerHandle;
  private readonly cancel: (timer: TimerHandle) => void;
  private disposed = false;

  constructor(options: RevisionedJobSaveOptions<T>) {
    this.options = options;
    this.debounceMs = options.debounceMs ?? 450;
    this.schedule = options.schedule ?? ((action, delayMs) => setTimeout(action, delayMs));
    this.cancel = options.cancel ?? ((timer) => clearTimeout(timer));
  }

  update(job: T): void {
    if (this.disposed) return;
    const jobId = this.options.idOf(job);
    if (this.retired.has(jobId)) return;
    const state = this.stateFor(jobId);
    this.setActive(jobId, state, true);
    const revision = ++state.revision;
    if (state.timer !== null) this.cancel(state.timer);
    state.timer = this.schedule(() => {
      // A canceled callback may still be delivered by a test scheduler or a
      // hostile host. It is not the current timer and must not clear the newer
      // debounce or its activity barrier.
      if (this.disposed || this.retired.has(jobId) || state.revision !== revision) return;
      state.timer = null;
      void this.enqueue(jobId, job, revision, true).catch(() => undefined);
    }, this.debounceMs);
  }

  commit(job: T): Promise<T> {
    if (this.disposed)
      return Promise.reject(new Error('The job save coordinator is no longer available.'));
    const jobId = this.options.idOf(job);
    if (this.retired.has(jobId))
      return Promise.reject(new Error('This job is being deleted and cannot be saved.'));
    const state = this.stateFor(jobId);
    this.setActive(jobId, state, true);
    const revision = ++state.revision;
    if (state.timer !== null) {
      this.cancel(state.timer);
      state.timer = null;
    }
    if (state.lastSaved === null) state.lastSaved = this.options.currentOf?.(jobId) ?? null;
    this.options.onCommitAdmitted?.(job);
    return this.enqueue(jobId, job, revision, false);
  }

  /**
   * Permanently closes this coordinator's write lane for one job and waits for
   * any save that already crossed the IPC boundary. The revision change makes
   * that late reply observationally inert; waiting for the tail ensures no
   * upsert can arrive after the engine has deleted the job.
   */
  async retire(jobId: string): Promise<void> {
    if (this.disposed) return;
    this.retired.add(jobId);
    const state = this.stateFor(jobId);
    this.setActive(jobId, state, true);
    state.revision += 1;
    if (state.timer !== null) {
      this.cancel(state.timer);
      state.timer = null;
    }
    await state.tail;
  }

  /** Reopens a retired lane only when the durable delete was refused. */
  resume(jobId: string): void {
    if (this.disposed) return;
    this.retired.delete(jobId);
    const state = this.states.get(jobId);
    if (state && state.timer === null) this.setActive(jobId, state, false);
  }

  /** Releases the retained deletion barrier after durable deletion succeeds. */
  completeRetire(jobId: string): void {
    if (!this.retired.delete(jobId)) return;
    const state = this.states.get(jobId);
    if (!state) return;
    this.setActive(jobId, state, false);
    if (this.states.get(jobId) === state) this.states.delete(jobId);
  }

  dispose(): void {
    this.disposed = true;
    for (const [jobId, state] of this.states) {
      state.revision += 1;
      if (state.timer !== null) this.cancel(state.timer);
      this.setActive(jobId, state, false);
    }
    this.states.clear();
    this.retired.clear();
  }

  private stateFor(jobId: string): JobSaveState<T> {
    const current = this.states.get(jobId);
    if (current) return current;
    const created: JobSaveState<T> = {
      revision: 0,
      timer: null,
      tail: Promise.resolve(),
      active: false,
      lastSaved: null,
    };
    this.states.set(jobId, created);
    return created;
  }

  private setActive(jobId: string, state: JobSaveState<T>, active: boolean): void {
    if (state.active === active) return;
    state.active = active;
    this.options.onActivityChange?.(jobId, active);
  }

  // standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  private enqueue(jobId: string, job: T, revision: number, autosave: boolean): Promise<T> {
    const state = this.stateFor(jobId);
    const operation = state.tail.then(async () => {
      try {
        try {
          const saved = await this.options.save(job);
          if (this.options.idOf(saved) !== jobId)
            throw new Error('The saved job identity did not match the requested job.');
          state.lastSaved = saved;
          if (!this.disposed && state.revision === revision) this.options.onSaved(saved);
          return saved;
        } catch (reason) {
          if (!this.disposed && state.revision === revision) {
            try {
              const authoritative = await this.options.reload(jobId);
              if (!this.disposed && state.revision === revision)
                this.options.onRollback(jobId, authoritative);
            } catch {
              // The original save error is the actionable failure. The reload is
              // best effort, but the failed change must not stay published: the
              // next whole-job save would store it silently. Put back the job as
              // last saved (or as published before the first commit) instead.
              if (!this.disposed && state.revision === revision && state.lastSaved !== null)
                this.options.onRollback(jobId, state.lastSaved);
            }
          }
          if (autosave && !this.disposed && state.revision === revision)
            this.options.onAutosaveError(reason);
          throw reason;
        }
      } finally {
        this.options.onSettled?.(job);
        if (
          !this.disposed &&
          !this.retired.has(jobId) &&
          this.states.get(jobId) === state &&
          state.revision === revision &&
          state.timer === null
        )
          this.setActive(jobId, state, false);
      }
    });
    state.tail = operation.then(
      () => undefined,
      () => undefined,
    );
    return operation;
  }
}
